import React, { useMemo } from 'react';

import { SideNavProvider } from '@atlassian/conversation-assistant';

import { isInRovoFullscreenPages } from '@townsquare/chat-view/__router-route';
import { LazyRovoSidebarNavigation } from '@townsquare/griffin-navigation/rovo-sidebar-navigation';
import { useRouter } from '@townsquare/router/useRouter';
import { useRovoAppification } from '@townsquare/rovo/appification';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';
import { SearchSidebarNavigation } from '@townsquare/search-sidebar-navigation';
import { fg } from '@townsquare/stat-sig/gate';

import { HomeSideNav } from '../navigation/HomeSideNav';
import { ChatHistory } from '../navigation/Rovo/ChatHistory';
import { RovoAppSideNav } from '../navigation/RovoSideNav';
import { useSearchSideNavItems } from '../sidebar/use-search-side-nav-items';

import { useIsOnFullscreenSearch, useIsOnRovoSearch } from './hooks';

export const useDisableRovoSearchSideNav = (): boolean => {
	const isOnRovoSearchRoute = useIsOnRovoSearch();
	const { rovoAppificationState } = useRovoAppification();

	// In Rovo appification we don't want special case behavior for the search page
	if (fg('townsquare_rovo_appification_sidenav_tweaks') && rovoAppificationState === 'enabled') {
		return false;
	}

	return isOnRovoSearchRoute;
};

type Props = {
	/**
	 * Prop to disable customization of the Home sidebar, for testing purposes.
	 */
	customizationDisabled?: boolean;
};

export function ConditionalSideNav(props: Props): React.JSX.Element | null {
	const searchSidebarItems = useSearchSideNavItems();
	const [routerState] = useRouter();
	const inRovoFullScreenPages = isInRovoFullscreenPages(routerState.route.name);
	const isOnRovoSearchRoute = useIsOnRovoSearch();
	const isOnFullscreenSearchRoute = useIsOnFullscreenSearch();
	const isOnSearchRoute = isOnRovoSearchRoute || isOnFullscreenSearchRoute;
	const { data: rovoEntitlement } = useRovoEntitlement();
	const disableSideNavRovoSearch = useDisableRovoSearchSideNav();
	const { rovoAppificationState } = useRovoAppification();

	// Memoize so GlobalSideNavigation doesn't see a new element reference on every render.
	// Without this, a new JSX element is created inline on every render, which causes
	// GlobalSideNavigation to unmount/remount ChatHistory (and its ConversationAssistantInit)
	// continuously, preventing storeInit from ever completing and blocking conversations/spaces
	// from being fetched.
	const chatHistoryElem = useMemo(
		() => <ChatHistory isDefaultExpanded={inRovoFullScreenPages} />,
		[inRovoFullScreenPages],
	);

	if (rovoAppificationState === 'enabled') {
		return (
			<SideNavProvider>
				<RovoAppSideNav postItemsElem={chatHistoryElem} {...props} />
			</SideNavProvider>
		);
	} else if (rovoAppificationState === 'disabled') {
		// We have to check disabled explicitly because we don't want these special cases to flicker during loading

		if (inRovoFullScreenPages && rovoEntitlement?.isRovoLLMEnabled) {
			return <LazyRovoSidebarNavigation />;
		}

		// don't show side nav for rovo search
		if (isOnSearchRoute) {
			return disableSideNavRovoSearch ? null : <SearchSidebarNavigation items={searchSidebarItems} />;
		}
	}

	return <HomeSideNav {...props} />;
}
